import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, where, getDocs, writeBatch } from 'firebase/firestore';
import { ActivityLog } from '../../services/activityLogger';
import { Clock, User, Shield, Activity, Calendar, Search, ChevronDown, Trash2 } from 'lucide-react';

interface AdminActivityLogProps {
  db: any;
  getCollectionPath: (col: string) => string;
  userId: string; // organizationId
}

export function AdminActivityLog({ db, getCollectionPath, userId }: AdminActivityLogProps) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [displayCount, setDisplayCount] = useState(15);

  // TEST PURPOSES: Bulk delete bookings
  const [bulkDeleteStart, setBulkDeleteStart] = useState('');
  const [bulkDeleteEnd, setBulkDeleteEnd] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteMessage, setBulkDeleteMessage] = useState('');

  const handleBulkDelete = async () => {
    if (!bulkDeleteStart || !bulkDeleteEnd) {
      setBulkDeleteMessage('Por favor selecciona ambas fechas.');
      return;
    }
    if (!await window.confirm(`⚠️ ADVERTENCIA ⚠️\n\nEstás a punto de borrar TODOS los turnos (pagados, confirmados y disponibles) del club desde el ${bulkDeleteStart} hasta el ${bulkDeleteEnd}.\n\n¿Estás completamente seguro? Esta acción NO SE PUEDE DESHACER.`)) return;
    
    setBulkDeleting(true);
    setBulkDeleteMessage('Buscando turnos...');
    try {
      const q = query(
        collection(db, getCollectionPath('bookings')),
        where('clubId', '==', userId),
        where('date', '>=', bulkDeleteStart),
        where('date', '<=', bulkDeleteEnd)
      );
      
      const snap = await getDocs(q);
      if (snap.empty) {
        setBulkDeleteMessage('No se encontraron turnos en ese rango.');
        setBulkDeleting(false);
        return;
      }

      setBulkDeleteMessage(`Borrando ${snap.size} turnos...`);
      
      // Firestore batch limit is 500
      const batches = [];
      let currentBatch = writeBatch(db);
      let opCount = 0;

      snap.docs.forEach((docSnap) => {
        currentBatch.delete(docSnap.ref);
        opCount++;
        if (opCount === 490) {
          batches.push(currentBatch);
          currentBatch = writeBatch(db);
          opCount = 0;
        }
      });
      if (opCount > 0) batches.push(currentBatch);

      for (const batch of batches) {
        await batch.commit();
      }

      setBulkDeleteMessage(`¡Éxito! Se borraron ${snap.size} turnos correctamente.`);
    } catch (e) {
      console.error(e);
      setBulkDeleteMessage('Error al borrar los turnos.');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setDisplayCount(15);
  }, [searchQuery, filterDate]);

  useEffect(() => {
    if (!db || !userId) {
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, getCollectionPath('activity_logs')),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const fetchedLogs: ActivityLog[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as ActivityLog;
          if (data.organizationId === userId) {
            fetchedLogs.push({ id: doc.id, ...data });
          }
        });
        setLogs(fetchedLogs);
        setLoading(false);
      }, (err) => {
        console.warn('Error al escuchar activity_logs:', err);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (e) {
      console.warn('Error al inicializar query activity_logs:', e);
      setLoading(false);
    }
  }, [db, userId, getCollectionPath]);

  const filteredLogs = logs.filter(log => {
    let match = true;
    
    if (filterDate) {
      // log.createdAt is a timestamp. Format it to YYYY-MM-DD
      const logDate = new Date(log.createdAt);
      // Prevent timezone offset issues by taking the local date
      const logDateStr = new Date(logDate.getTime() - (logDate.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
      if (logDateStr !== filterDate) match = false;
    }

    if (match && searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      match = (
        (log.activity && log.activity.toLowerCase().includes(q)) ||
        (log.userName && log.userName.toLowerCase().includes(q)) ||
        (log.userRole && log.userRole.toLowerCase().includes(q)) ||
        (log.dateFormatted && log.dateFormatted.toLowerCase().includes(q)) ||
        (log.timeFormatted && log.timeFormatted.toLowerCase().includes(q))
      );
    }
    
    return match;
  });

  const displayedLogs = filteredLogs.slice(0, displayCount);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="bg-black/20 p-6 rounded-[var(--radius-large)] border border-white/10">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl theme-primary-bg/10 flex items-center justify-center shrink-0">
              <Activity className="theme-primary-text" size={20} />
            </div>
            <div>
              <h3 className="text-xl font-black uppercase tracking-wider leading-none">Registro de Actividad</h3>
              <p className="text-[10px] opacity-40 font-bold uppercase tracking-widest mt-1">
                Historial de eventos y acciones del staff del club
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-[140px]">
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-xs font-mono outline-none focus:theme-primary-border text-white transition-all [color-scheme:dark]"
              />
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por acción, usuario, rol..."
                className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-xs font-mono outline-none focus:theme-primary-border placeholder:text-white/30 text-white transition-all"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12 opacity-50">
            <p className="text-xs font-bold uppercase tracking-widest animate-pulse">Cargando registro...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12 bg-black/40 rounded-2xl border border-white/5">
            <p className="text-xs font-bold opacity-40 uppercase tracking-widest">
              {logs.length === 0 ? 'No hay actividades registradas aún' : 'No se encontraron resultados para tu búsqueda'}
            </p>
          </div>
        ) : (
          <div className="space-y-2.5 overflow-x-auto">
            {displayedLogs.map((log) => (
              <div
                key={log.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3.5 bg-black/40 hover:bg-black/60 rounded-xl border border-white/5 transition-all text-xs font-mono tracking-tight"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="opacity-40 shrink-0 flex items-center gap-1 font-bold text-[11px]">
                    <Calendar size={12} /> {log.dateFormatted}, {log.timeFormatted}
                  </span>
                  <span className="text-white font-bold flex items-center gap-1 shrink-0">
                    <User size={12} className="theme-primary-text opacity-70" /> {log.userName}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-white/5 text-[10px] font-bold text-white/60 tracking-wider uppercase shrink-0">
                    {log.userRole}
                  </span>
                </div>
                <div className="text-white/90 sm:text-right font-sans font-medium text-xs pl-6 sm:pl-0">
                  {log.activity}
                </div>
              </div>
            ))}
            
            {displayCount < filteredLogs.length && (
              <div className="pt-4 flex justify-center">
                <button
                  onClick={() => setDisplayCount(prev => prev + 15)}
                  className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold uppercase tracking-wider text-[10px] px-6 py-2.5 rounded-full transition-all"
                >
                  <ChevronDown size={14} />
                  Ver Más Registros
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ZONA DE PRUEBAS (TEMPORAL) - Muy discreto */}
      <div className="mt-8 mb-4 opacity-10 hover:opacity-100 transition-opacity flex flex-col items-center">
        <div className="flex items-center gap-1.5 mb-2">
           <Trash2 size={10} className="text-red-500/50" />
           <span className="text-[9px] font-black text-zinc-600 uppercase tracking-widest">Wipe Bookings</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={bulkDeleteStart}
            onChange={e => setBulkDeleteStart(e.target.value)}
            className="bg-transparent border border-white/10 rounded px-2 py-1 text-[10px] text-zinc-500 focus:outline-none"
          />
          <span className="text-zinc-600 text-[9px]">a</span>
          <input
            type="date"
            value={bulkDeleteEnd}
            onChange={e => setBulkDeleteEnd(e.target.value)}
            className="bg-transparent border border-white/10 rounded px-2 py-1 text-[10px] text-zinc-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={bulkDeleting || !bulkDeleteStart || !bulkDeleteEnd}
            className="px-3 py-1 bg-red-500/10 hover:bg-red-500/30 text-red-500/60 rounded font-black text-[9px] uppercase tracking-wider transition-all disabled:opacity-50"
          >
            {bulkDeleting ? '...' : 'Clear'}
          </button>
        </div>
        {bulkDeleteMessage && (
          <p className="mt-1 text-[9px] font-bold text-red-500/70">
            {bulkDeleteMessage}
          </p>
        )}
      </div>
    </div>
  );
}
